'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  type LanguageCode,
  loadUILanguage,
  saveUILanguage,
  loadContentTranslationEnabled,
  saveContentTranslationEnabled,
  loadGuestLanguages,
  saveGuestLanguages,
  t as translate,
} from '@/lib/i18n';

interface LanguageContextValue {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  contentTranslationEnabled: boolean;
  setContentTranslationEnabled: (enabled: boolean) => void;
  guestLanguages: LanguageCode[];
  setGuestLanguages: (langs: LanguageCode[]) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'nl',
  setLanguage: () => {},
  contentTranslationEnabled: false,
  setContentTranslationEnabled: () => {},
  guestLanguages: ['nl', 'en'],
  setGuestLanguages: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>('nl');
  const [contentTranslationEnabled, setContentTranslationEnabledState] = useState(false);
  const [guestLanguages, setGuestLanguagesState] = useState<LanguageCode[]>(['nl', 'en']);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setLanguageState(loadUILanguage());
    setContentTranslationEnabledState(loadContentTranslationEnabled());
    setGuestLanguagesState(loadGuestLanguages());
    setMounted(true);
  }, []);

  const setLanguage = useCallback((lang: LanguageCode) => {
    setLanguageState(lang);
    saveUILanguage(lang);
  }, []);

  const setContentTranslationEnabled = useCallback((enabled: boolean) => {
    setContentTranslationEnabledState(enabled);
    saveContentTranslationEnabled(enabled);
  }, []);

  const setGuestLanguages = useCallback((langs: LanguageCode[]) => {
    setGuestLanguagesState(langs);
    saveGuestLanguages(langs);
  }, []);

  const tFn = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(key, language, params),
    [language]
  );

  if (!mounted) {
    return (
      <LanguageContext.Provider value={{
        language: 'nl',
        setLanguage,
        contentTranslationEnabled: false,
        setContentTranslationEnabled,
        guestLanguages: ['nl', 'en'],
        setGuestLanguages,
        t: (key) => translate(key, 'nl'),
      }}>
        {children}
      </LanguageContext.Provider>
    );
  }

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      contentTranslationEnabled,
      setContentTranslationEnabled,
      guestLanguages,
      setGuestLanguages,
      t: tFn,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
